import { ReactElement } from "react";
import ButtonFactory from "@components/Button/ButtonFactory";
import { ButtonType } from "@components/Button/utils/types";
import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";
import { getExploreMoreObjectByTags } from "@pages/Article/components/ArticleExploreMore/helpers";
import { FeedTag } from "@pages/Keyword/utils/types";
import { cn } from "@util/helpers";

export type ArticleExploreMoreProps = {
  /** Array of keywords to explore more articles */
  keyword: FeedTag[];
  /** Optional CSS class to apply to the component */
  testid?: string;
};

/**
 * ArticleExploreMore component that displays a section with more articles
 * related to the provided keywords.
 */
const ArticleExploreMore = ({
  keyword,
}: ArticleExploreMoreProps): ReactElement => {
  const sendDataToGTM = useGTMDispatch();
  const dataObject = getExploreMoreObjectByTags(keyword)?.[0];

  if (!dataObject) return <></>;

  const handleOnClickLink = () => {
    sendDataToGTM(dataObject.trackingEvents);
  };

  return (
    <div
      data-testid="article-explore-more-component"
      className="mb-6 border border-gray-175 p-6 md:p-8"
    >
      <h5 className="mb-6 border-b border-gray-850 pb-2 !font-poppins text-base font-semibold uppercase leading-normal tracking-5% text-gray-850 md:text-lg">
        {dataObject.title}
      </h5>

      <div className="flex py-4 md:px-10 md:py-6">
        <img
          alt="Newsletter Img"
          className="hidden h-auto max-w-full self-start min-[321px]:block"
          width="75"
          height="75"
          src={dataObject.thumbnail}
        />

        <div className="min-[321px]:pl-4 md:pl-6">
          <p className="mb-3 font-public-sans text-base font-light tracking-normal text-gray-850 md:text-lg">
            {dataObject.description}
          </p>

          <ButtonFactory
            buttonType={ButtonType.Link}
            text={dataObject.buttonLabel}
            link={dataObject.link}
            onClick={handleOnClickLink}
            mask={ButtonType.Secondary}
            className={cn(
              "hidden w-full min-w-[155px] text-center !no-underline md:block md:max-w-[155px]"
            )}
          />
        </div>
      </div>

      <ButtonFactory
        buttonType={ButtonType.Link}
        text={dataObject.buttonLabel}
        link={dataObject.link}
        onClick={handleOnClickLink}
        mask={ButtonType.Secondary}
        className={cn("flex w-full justify-center !no-underline md:hidden")}
      />
    </div>
  );
};

export default ArticleExploreMore;
